import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", address: "", gstin: "", phone: "", party_type: "both" };

export default function Parties() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/parties").then(({data})=>setItems(data));
  useEffect(()=>{ load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ name: p.name, address: p.address, gstin: p.gstin, phone: p.phone, party_type: p.party_type }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    try {
      if (editId) await api.put(`/parties/${editId}`, form);
      else await api.post("/parties", form);
      toast.success("Saved"); setOpen(false); load();
    } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => { if(!confirm("Delete party?")) return; await api.delete(`/parties/${id}`); load(); };

  return (
    <div className="p-8" data-testid="parties-page">
      <div className="flex items-start justify-between mb-6">
        <div><h1 className="font-display text-3xl text-slate-900">Parties</h1><p className="text-slate-500 text-sm">Consignors & Consignees</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="btn-primary" data-testid="new-party-btn"><Plus size={16}/> New Party</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Edit Party" : "New Party"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="party-name-input"/></div>
              <div><Label>Address</Label><Textarea value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} data-testid="party-address-input"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e)=>setForm({...form, gstin: e.target.value})} data-testid="party-gstin-input"/></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid="party-phone-input"/></div>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.party_type} onValueChange={(v)=>setForm({...form, party_type: v})}>
                  <SelectTrigger data-testid="party-type-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consignor">Consignor</SelectItem>
                    <SelectItem value="consignee">Consignee</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="btn-primary" data-testid="save-party-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="text-left px-4 py-2">Name</th>
            <th className="text-left px-4 py-2">GSTIN</th>
            <th className="text-left px-4 py-2">Phone</th>
            <th className="text-left px-4 py-2">Type</th>
            <th className="text-right px-4 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {items.length===0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No parties yet.</td></tr>}
            {items.map(p=>(
              <tr key={p.id} className="border-t border-slate-100" data-testid={`party-row-${p.id}`}>
                <td className="px-4 py-2 font-medium" data-testid={`party-name-${p.id}`}>{p.name}</td>
                <td className="px-4 py-2">{p.gstin}</td>
                <td className="px-4 py-2">{p.phone}</td>
                <td className="px-4 py-2 capitalize">{p.party_type}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={()=>openEdit(p)} className="p-2 hover:bg-slate-100 rounded"><Pencil size={16}/></button>
                  <button onClick={()=>remove(p.id)} className="p-2 hover:bg-red-50 rounded text-red-600"><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
